import type { Content } from "@/app/queries";
import { plural } from "@/lib/plural";
import { DashedButton, IconButton, INPUT, quizItems, type Patch, type QA } from "./shared";

type Block = Content["blocks"][string];

/** «Проверь себя» парами вопрос–ответ. text держим производным от вопросов:
 *  сервер мигрирует непустой text в items, если items пусты, — удалённые
 *  вопросы иначе вернулись бы из старого text. */
export function QuizEditor({ block, patch }: { block: Block; patch: (p: Patch) => void }) {
  const items = quizItems(block);
  const n = items.length;
  const unanswered = items.filter((x) => x.a.trim() === "").length;
  const write = (fn: (xs: QA[]) => QA[]) =>
    patch((c) => {
      const cur = c.blocks.quiz ?? { on: false, text: "" };
      const next = fn(quizItems(cur));
      return { ...c, blocks: { ...c.blocks, quiz: { ...cur, items: next, text: next.map((x) => x.q).join("\n") } } };
    });
  const setItem = (i: number, key: "q" | "a", value: string) => write((xs) => xs.map((x, j) => (j === i ? { ...x, [key]: value } : x)));
  return (
    <div className="flex flex-col gap-2.5">
      <div className="font-mono text-[11px]" style={{ color: unanswered > 0 ? "var(--warn)" : "var(--mut)" }}>
        {`${n} ${plural(n, "вопрос", "вопроса", "вопросов")}${unanswered > 0 ? ` · ${unanswered} без ответа` : ""}`}
      </div>
      {items.map((x, i) => (
        <div key={x.id || `q-${i}`} className="grid grid-cols-[22px_minmax(0,1fr)_26px] items-start gap-2.5 rounded-sm border border-bd px-2.5 py-2" data-testid="quiz-item">
          <span className="pt-2 font-mono text-[11px] text-mut">{i + 1}</span>
          <div className="flex flex-col gap-1.5">
            <input aria-label={`вопрос ${i + 1}`} value={x.q} placeholder="вопрос" onChange={(e) => setItem(i, "q", e.target.value)} className={`${INPUT} w-full font-sans text-[13px]`} />
            <textarea
              aria-label={`ответ ${i + 1}`}
              rows={2}
              value={x.a}
              placeholder="ответ — студент откроет его кнопкой «показать ответ»"
              onChange={(e) => setItem(i, "a", e.target.value)}
              className={`${INPUT} w-full resize-y font-sans text-[12.5px] leading-[1.5] text-ink2`}
            />
          </div>
          <IconButton onClick={() => write((xs) => xs.filter((_, j) => j !== i))} aria-label={`удалить вопрос ${i + 1}`} className="pt-2">
            ×
          </IconButton>
        </div>
      ))}
      {n === 0 && <div className="text-[12.5px] text-ink2">Вопросов пока нет — блок не покажется студенту, пока пуст.</div>}
      <DashedButton className="w-fit" onClick={() => write((xs) => [...xs, { id: "", q: "", a: "" }])}>
        + вопрос
      </DashedButton>
    </div>
  );
}
